import HockeyLineups from "../sports/HockeyLineups";
import "../sports/hockey.css";
import { HQ, requestedHockeyMatch } from "../sportConfig";
import ShareButton from "../ShareButton";
import PlayerPosition from "./PlayerPosition";
import MatchSimulation from "./MatchSimulation";
import MatchTipPulse from "./MatchTipPulse";
import React, { useEffect, useRef, useState } from "react";
import usePlayerDialog from "../usePlayerDialog";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles.css";
import { useFavoriteTeams } from "../FavoriteTeams";

export const resolveMatchUrl = (url, status) => {
    if (!url || !/^[1-9]\d*$/.test(String(status?.season_id || ""))) return url;
    const source = url.match(/^https:\/\/stats\.innebandy\.se\/sasong\/(?:None|null|undefined)\/serie\/(\d+)\/match\/\d+(?:\/|$)/);
    if (!source || String(status.competition_id) !== source[1]) return url;
    return url.replace(/\/sasong\/(?:None|null|undefined)\//, `/sasong/${status.season_id}/`);
};

const API_URL = `${HQ.dataApi}/lineup`;
const STATUS_API_URL = `${HQ.dataApi}/match-status`;
const STANDINGS_API_URL = `${HQ.dataApi}/standings`;
const periodLabel = (period) => Number(period) === 4 ? "Förlängning" : Number(period) === 5 ? "Straffläggning" : `Period ${period}`;

const playerProfileCache = new Map();

const positionShort = (position = "") => {
    const normalized = position.toLowerCase().replace(/[\s_-]+/g, "");
    if (normalized.includes("vänster") && normalized.includes("forward")) return "VF";
    if (normalized.includes("höger") && normalized.includes("forward")) return "HF";
    if (normalized.includes("left") && normalized.includes("forward")) return "LF";
    if (normalized.includes("right") && normalized.includes("forward")) return "RF";
    if (normalized.includes("vänster") && normalized.includes("back")) return "VB";
    if (normalized.includes("höger") && normalized.includes("back")) return "HB";
    if (normalized.includes("left") && normalized.includes("back")) return "LB";
    if (normalized.includes("right") && normalized.includes("back")) return "RB";
    switch (normalized) {
        case "forward": return "FW";
        case "center": return "C";
        case "målvakt": return "MV";
        case "back": return "B";
        default: return position || "Ej angiven";
    }
};

const explicitSide = (position = "") => {
    const normalized = position.toLowerCase().replace(/[\s_-]+/g, "");
    if (normalized.includes("vänster") || normalized.includes("left")) return "left";
    if (normalized.includes("höger") || normalized.includes("right")) return "right";
    return null;
};

const placePlayers = (players) => {
    const placements = players.map((player) => ({ player, side: explicitSide(player.position) }));
    const occupied = new Set(placements.map(({ side }) => side).filter(Boolean));
    const preference = players.length === 1
        ? ["center", "left", "right"]
        : players.length === 2
            ? ["left", "right", "center"]
            : ["left", "center", "right"];
    placements.forEach((placement) => {
        if (placement.side) return;
        placement.side = preference.find((side) => !occupied.has(side)) || null;
        if (placement.side) occupied.add(placement.side);
    });
    return placements;
};

const PlayerCard = ({ player, status, side, onSelect }) => (
    <button
        type="button"
        className={`formation-player${status === "Startar" ? " formation-player--starter" : ""}${side ? ` formation-player--${side}` : ""}`}
        onClick={() => onSelect(player)}
        title={`Visa profil för ${player.name}`}
    >
        {status && <span className={`player-status player-status--${status.toLowerCase()}`}>{status}</span>}
        <span className="formation-player__number">#{player.number}</span>
        <span className="formation-player__name">{player.name}</span>
        <span className="formation-player__meta">
            {positionShort(player.position)}
            {player.captain && <span className="captain-badge" title="Lagkapten">K</span>}
        </span>
    </button>
);

const PositionRow = ({ className, players, getStatus, onSelect }) => {
    if (!players.length) return null;
    const count = Math.min(players.length, 4);
    const usesSides = className === "forwards" || className === "defense";
    const placements = usesSides
        ? placePlayers(players)
        : players.map((player) => ({ player, side: null }));
    const hasSides = placements.some(({ side }) => side === "left" || side === "right");
    return (
        <div className={`formation-row ${className} formation-row--count-${count}${hasSides ? " formation-row--sided" : ""}`}>
            {placements.map(({ player, side }, index) => (
                <PlayerCard
                    key={`${player.number}-${player.name}-${index}`}
                    player={player}
                    status={getStatus ? getStatus(player) : null}
                    side={side}
                    onSelect={onSelect}
                />
            ))}
        </div>
    );
};

const LineCard = ({ line, formation, onSelect }) => {
    const hasPositionedPlayers =
        formation.forwards.length + formation.centers.length + formation.backs.length > 0;
    return (
        <article className="line-card">
            <header className="line-card__header">
                <span className="line-card__eyebrow">Formation</span>
                <h3>{`Femma ${line}`}</h3>
            </header>
            {hasPositionedPlayers && (
                <div className="formation-rink">
                    <PositionRow className="forwards" players={formation.forwards} onSelect={onSelect} />
                    <PositionRow className="midfield" players={formation.centers} onSelect={onSelect} />
                    <PositionRow className="defense" players={formation.backs} onSelect={onSelect} />
                </div>
            )}
            {formation.others.length > 0 && (
                <div className="unpositioned-players">
                    {formation.others.map((player, index) => (
                        <PlayerCard key={`${player.number}-${player.name}-${index}`} player={player} onSelect={onSelect} />
                    ))}
                </div>
            )}
        </article>
    );
};

const UnassignedCard = ({ formation, onSelect }) => {
    const players = [
        ...formation.forwards,
        ...formation.centers,
        ...formation.backs,
        ...formation.others,
    ];
    if (!players.length) return null;
    return (
        <article className="unassigned-card">
            <div className="unassigned-card__heading">
                <span className="line-card__eyebrow">Ingen femma angiven</span>
                <h3>Övriga utespelare</h3>
            </div>
            <div className="unassigned-player-grid">
                {players.map((player, index) => (
                    <PlayerCard key={`${player.number}-${player.name}-${index}`} player={player} onSelect={onSelect} />
                ))}
            </div>
        </article>
    );
};

const TeamLineup = ({ team, onSelectPlayer }) => {
    if (!team.players || team.players.length === 0) {
        return (
            <section className="team-lineup">
                <div className="team-lineup-header">
                    {team.logo && <img src={team.logo} alt="" className="team-lineup-logo" />}
                    <h2 className="team-lineup-name">{team.name}</h2>
                </div>
                <p className="no-matches">Laguppställningen har inte publicerats ännu.</p>
            </section>
        );
    }

    const lines = {};
    const goalkeepers = [];
    team.players.forEach((player) => {
        if ((player.position || "").toLowerCase() === "målvakt") {
            goalkeepers.push(player);
            return;
        }
        const line = player.line || "Övriga";
        if (!lines[line]) {
            lines[line] = { backs: [], centers: [], forwards: [], others: [] };
        }
        const position = (player.position || "").toLowerCase();
        if (position.includes("forward")) lines[line].forwards.push(player);
        else if (position.includes("back")) lines[line].backs.push(player);
        else if (position.includes("center")) lines[line].centers.push(player);
        else lines[line].others.push(player);
    });

    const sortedLines = Object.keys(lines).sort((left, right) => {
        if (left === "Övriga") return 1;
        if (right === "Övriga") return -1;
        return Number(left) - Number(right);
    });
    goalkeepers.sort((left, right) => Number(left.line || 999) - Number(right.line || 999));
    const selectPlayer = (player) => onSelectPlayer({ ...player, team_name: team.name });

    return (
        <section className="team-lineup">
            <div className="team-lineup-header">
                {team.logo && <img src={team.logo} alt="" className="team-lineup-logo" />}
                <div>
                    <span className="team-lineup-kicker">Laguppställning</span>
                    <h2 className="team-lineup-name">{team.name}</h2>
                </div>
            </div>
            <div className="line-cards">
                {goalkeepers.length > 0 && (
                    <article className="goalkeeper-card">
                        <div className="goalkeeper-card__heading">
                            <span className="line-card__eyebrow">Sista utpost</span>
                            <h3>Målvakter</h3>
                        </div>
                        <PositionRow
                            className="goalkeeper-list"
                            players={goalkeepers}
                            getStatus={(player) => player.line === "Övriga" ? null : player.line === "1" ? "Startar" : "Reserv"}
                            onSelect={selectPlayer}
                        />
                    </article>
                )}
                {sortedLines.map((line) => (
                    line === "Övriga"
                        ? <UnassignedCard key={line} formation={lines[line]} onSelect={selectPlayer} />
                        : <LineCard key={line} line={line} formation={lines[line]} onSelect={selectPlayer} />
                ))}
            </div>
        </section>
    );
};

export const PlayerProfileWidget = ({ player, onClose, onPositionChange }) => {
    const backdrop = usePlayerDialog();
    const [profile, setProfile] = useState(() => playerProfileCache.get(player.id) || player);
    const [loadingProfile, setLoadingProfile] = useState(Boolean(player.id && !playerProfileCache.has(player.id)));
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        if (!player.id || playerProfileCache.has(player.id)) {
            setLoadingProfile(false);
            return undefined;
        }

        const controller = new AbortController();
        let cancelled = false;
        fetch(`${HQ.dataApi}/player/${encodeURIComponent(player.id)}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Nätverksfel");
                return response.json();
            })
            .then((data) => {
                if (cancelled) return;
                const enrichedProfile = { ...player, ...data, ibis_position: player.ibis_position ?? data.position };
                playerProfileCache.set(player.id, enrichedProfile);
                setProfile(enrichedProfile);
            })
            .catch((fetchError) => {
                if (fetchError.name !== "AbortError") {
                    console.error("Fel vid hämtning av spelarprofil:", fetchError);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingProfile(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [player]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [onClose]);


    const initials = profile.name
        .split(" ")
        .map((part) => part[0])
        .slice(0, 2)
        .join("");
    const profileUrl = player.id
        ? `https://stats.innebandy.se/spelare/${player.id}`
        : null;
    const birthYear = Number(profile.birth_year);
    const hasBirthYear = Number.isInteger(birthYear) && birthYear >= 1900 && birthYear <= new Date().getFullYear();

    return createPortal(
        <div ref={backdrop} className="player-profile-backdrop" role="presentation" onMouseDown={onClose}>
            <section
                className="player-profile-widget"
                role="dialog"
                aria-modal="true"
                aria-labelledby="player-profile-name"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <button className="player-profile-close" type="button" onClick={onClose} aria-label="Stäng spelarprofil">
                    ×
                </button>
                <div className="player-profile-photo-wrap">
                    {profile.image_url && !imageFailed ? (
                        <img
                            className="player-profile-photo"
                            src={profile.image_url}
                            alt={profile.name}
                            loading="lazy"
                            onError={() => setImageFailed(true)}
                        />
                    ) : (
                        <div className={`player-profile-placeholder${loadingProfile ? " player-profile-placeholder--loading" : ""}`} aria-hidden="true">
                            {initials}
                        </div>
                    )}
                </div>
                <div className="player-profile-content">
                    <span className="player-profile-kicker">#{profile.number} · {positionShort(player.position ?? profile.position)}</span>
                    <h2 id="player-profile-name">{profile.name}</h2>
                    <p>{profile.association || profile.team_name}{hasBirthYear && <span className="player-profile-birth-year" aria-label={`Födelseår ${birthYear}`}> · {birthYear}</span>}</p>
                    {player.id && <PlayerPosition player={{ ...profile, ...player, ibis_position: player.ibis_position ?? profile.ibis_position ?? profile.position }} onChange={onPositionChange} />}
                    {profileUrl ? (
                        <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="player-profile-link">
                            Officiell spelarprofil ↗
                        </a>
                    ) : (
                        <span className="player-profile-link player-profile-link--disabled">Officiell profil saknas</span>
                    )}
                </div>
            </section>
        </div>,
        document.body
    );
};

const MatchScore = ({ match, loading, hidden = false, teamPath }) => {
    const { teamIds, toggleTeam, ready, authenticated, notice } = useFavoriteTeams();
    if (!match && loading) return <div className="match-score match-score--loading">Hämtar aktuell ställning…</div>;
    if (!match) return null;
    const hasScore = match.home_goals !== null && match.home_goals !== undefined;
    const live = match.status_id === 2 || match.status_id === 3;
    const detail = match.status_id === 2 && match.period
        ? periodLabel(match.period)
        : match.status_id === 3 && match.period
            ? `Efter ${periodLabel(match.period).toLowerCase()}`
            : null;
    const latestGoal = [...(match.events || [])].reverse().find((event) => event.counts_as_goal);
    const scoringTeam = latestGoal && ([true, 1].includes(latestGoal.is_home) ? match.home_team : [false, 0].includes(latestGoal.is_home) ? match.away_team : null);
    return (
        <section className={`match-score${live ? " match-score--live" : ""}`} aria-live="polite">
            <div className="match-score__team match-score__team--home">
                {match.home_logo && <img src={match.home_logo} alt="" />}
                {match.home_team_id && teamPath ? <Link className="match-score__team-link" to={teamPath(match.home_team_id)}>{match.home_team}<span aria-hidden="true"> ↗</span></Link> : <span>{match.home_team}</span>}
                {match.home_team_id && <button type="button" className={`team-favorite${teamIds.includes(match.home_team_id) ? " team-favorite--active" : ""}`}
                    aria-label={`${teamIds.includes(match.home_team_id) ? "Ta bort" : "Lägg till"} ${match.home_team} ${teamIds.includes(match.home_team_id) ? "från" : "i"} Mina lag`}
                    aria-pressed={teamIds.includes(match.home_team_id)} title={teamIds.includes(match.home_team_id) ? "Ta bort från Mina lag" : authenticated ? "Spara i Mina lag" : "Spara i Mina lag på den här enheten"} disabled={!ready} onClick={() => toggleTeam(match.home_team_id)}>★</button>}
            </div>
            <div className="match-score__center">
                {hidden ? (
                    <span className="match-score__hidden">Resultat dolt</span>
                ) : <>
                <span className={`match-score__status${live ? " match-score__status--live" : ""}`}>
                    {match.label || "Match"}
                </span>
                <strong>{hasScore ? `${match.home_goals} – ${match.away_goals}` : "–"}</strong>
                {detail && <small>{detail}</small>}
                </>}
            </div>
            <div className="match-score__team match-score__team--away">
                {match.away_logo && <img src={match.away_logo} alt="" />}
                {match.away_team_id && teamPath ? <Link className="match-score__team-link" to={teamPath(match.away_team_id)}>{match.away_team}<span aria-hidden="true"> ↗</span></Link> : <span>{match.away_team}</span>}
                {match.away_team_id && <button type="button" className={`team-favorite${teamIds.includes(match.away_team_id) ? " team-favorite--active" : ""}`}
                    aria-label={`${teamIds.includes(match.away_team_id) ? "Ta bort" : "Lägg till"} ${match.away_team} ${teamIds.includes(match.away_team_id) ? "från" : "i"} Mina lag`}
                    aria-pressed={teamIds.includes(match.away_team_id)} title={teamIds.includes(match.away_team_id) ? "Ta bort från Mina lag" : authenticated ? "Spara i Mina lag" : "Spara i Mina lag på den här enheten"} disabled={!ready} onClick={() => toggleTeam(match.away_team_id)}>★</button>}
            </div>
            {!hidden && scoringTeam && <div className="match-score__last-goal">
                <span>MÅL</span>
                <strong>Senaste mål: {scoringTeam}</strong>
                <small>{latestGoal.period ? periodLabel(latestGoal.period) : "Period –"} · {latestGoal.minute ?? 0}:{String(latestGoal.second ?? 0).padStart(2, "0")}</small>
            </div>}
            {notice && <p className="match-score__notice" role="status">{notice}</p>}
        </section>
    );
};

const MatchInfo = ({ match }) => {
    if (!match) return null;
    const date = match.match_time && new Date(match.match_time);
    const time = date && !Number.isNaN(date.getTime())
        ? new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(date)
        : null;
    const isInnebandyPlay = match.broadcast_url?.startsWith("https://play.innebandy.se/");
    const hasDetails = match.referees?.length || match.spectators != null || match.match_number;
    if (!match.venue && !time && !hasDetails && !match.broadcast_url) return null;
    return (
        <div className="match-info">
            {match.venue && <span title="Hall">⌖ {match.venue}</span>}
            {time && <time dateTime={match.match_time}>◷ {time}</time>}
            {(hasDetails || match.broadcast_url) && <div className="match-info__actions">
                {hasDetails && <details>
                    <summary>Mer matchinfo</summary>
                    <div className="match-info__details">
                        {match.referees?.length > 0 && <span><strong>Domare:</strong> {match.referees.join(", ")}</span>}
                        {match.spectators != null && <span><strong>Publik:</strong> {match.spectators}</span>}
                        {match.match_number && <span><strong>Matchnummer:</strong> {match.match_number}</span>}
                    </div>
                </details>}
                {match.broadcast_url && <a className={`match-info__broadcast${isInnebandyPlay ? " match-info__broadcast--play" : ""}`} href={match.broadcast_url} target="_blank" rel="noopener noreferrer"
                    title={isInnebandyPlay ? "Se matchen på Innebandy Play" : "Se matchsändningen"}
                    aria-label={isInnebandyPlay ? "Se matchen på Innebandy Play (öppnas i ny flik)" : "Se matchsändningen (öppnas i ny flik)"}>
                    {isInnebandyPlay
                        ? <img className="match-info__play-logo" src="/floorball/innebandy-play.png" alt="" />
                        : <span className="match-info__play-icon" aria-hidden="true">▶</span>}
                    {!isInnebandyPlay && "Se sändning"}
                </a>}
            </div>}
        </div>
    );
};

const EventPlayer = ({ id, name, teamName, onSelect }) => {
    if (!name) return null;
    if (!id) return <span>{name}</span>;
    return (
        <button
            type="button"
            className="match-event__player"
            onClick={() => onSelect({ id, name, number: "–", team_name: teamName })}
        >
            {name}
        </button>
    );
};

const MatchEvents = ({ match, loading, onSelectPlayer }) => {
    if (loading && !match?.events) {
        return <div className="events-state">Hämtar matchhändelser…</div>;
    }
    const events = match?.events || [];
    const recordedPeriods = match?.periods || [];
    if (!events.length && !recordedPeriods.length && match?.status_id !== 4) {
        return <div className="events-state">Inga mål eller utvisningar är registrerade ännu.</div>;
    }
    const periods = events.reduce((groups, event) => {
        const period = event.period || 0;
        if (!groups[period]) groups[period] = [];
        groups[period].push(event);
        return groups;
    }, {});
    const visiblePeriods = match?.status_id === 4 ? [1, 2, 3, ...recordedPeriods] : recordedPeriods;
    visiblePeriods.forEach((period) => { if (!periods[period]) periods[period] = []; });
    const formatTime = (event) => `${event.minute ?? 0}:${String(event.second ?? 0).padStart(2, "0")}`;
    return (
        <section className="match-events" aria-label="Matchhändelser">
            {Object.entries(periods).map(([period, periodEvents]) => (
                <div className="match-period" key={period}>
                    <h2>{periodLabel(period)}</h2>
                    <div className="match-period__events">
                        {!periodEvents.length && <p className="match-period__empty">Inga mål eller utvisningar.</p>}
                        {periodEvents.map((event) => {
                            const teamName = [true, 1].includes(event.is_home) ? match.home_team : [false, 0].includes(event.is_home) ? match.away_team : "";
                            const isPenalty = event.type === "penalty";
                            const isMissedPenalty = event.type === "missed_penalty";
                            return (
                                <article
                                    className={`match-event${event.is_home === true ? " match-event--home" : event.is_home === false ? " match-event--away" : ""}`}
                                    key={event.id}
                                >
                                    <time>{formatTime(event)}</time>
                                    <span className={`match-event__icon match-event__icon--${event.type}`} aria-hidden="true">
                                        {isPenalty ? (event.penalty_duration || "U") : isMissedPenalty ? "×" : "●"}
                                    </span>
                                    <div className="match-event__content">
                                        <strong>{event.label}</strong>
                                        {teamName && <span className="match-event__team">{teamName}</span>}
                                        <EventPlayer
                                            id={event.player_id}
                                            name={event.player_name}
                                            teamName={teamName}
                                            onSelect={onSelectPlayer}
                                        />
                                        {event.counts_as_goal && event.assist_name && (
                                            <small>
                                                Assist: <EventPlayer
                                                    id={event.assist_id}
                                                    name={event.assist_name}
                                                    teamName={teamName}
                                                    onSelect={onSelectPlayer}
                                                />
                                            </small>
                                        )}
                                        {isPenalty && event.penalty && <small>{event.penalty}</small>}
                                    </div>
                                    {event.counts_as_goal && (
                                        <span className="match-event__score">{event.home_goals}–{event.away_goals}</span>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </div>
            ))}
        </section>
    );
};

const Standings = ({ data, loading, error, match, serie, returnTo }) => {
    if (loading) return <div className="events-state">Hämtar tabellen…</div>;
    if (error) return <div className="events-state">Tabellen kunde inte hämtas just nu.</div>;
    if (!data?.standings?.length) return <div className="events-state">Ingen tabell har publicerats.</div>;
    const matchTeams = new Set([match?.home_team, match?.away_team]);
    return (
        <section className="standings-card">
            <header><h2>{match?.competition_name || "Tabell"}</h2><span>M = matcher · +/- = målskillnad</span></header>
            <div className="standings-table" role="table" aria-label="Serietabell">
                <div className="standings-row standings-row--head" role="row">
                    <span>#</span><span>Lag</span><span>M</span><span>+/-</span><span>Poäng</span>
                </div>
                {data.standings.map((row) => (
                    <div className={`standings-row${matchTeams.has(row.team) ? " standings-row--current" : ""}`} role="row" key={row.team_id}>
                        <strong>{row.position}</strong>
                        <Link className="standings-team standings-team--link" to={`/team/${encodeURIComponent(row.team_id)}?serie=${encodeURIComponent(serie)}&competition=${match?.competition_id || ""}&league=${encodeURIComponent(match?.competition_name || "")}&returnTo=${encodeURIComponent(returnTo)}`} aria-label={`Visa ${row.team}`}>{row.logo && <img src={row.logo} alt="" />}<span>{row.team}</span><span className="standings-team__arrow" aria-hidden="true">›</span></Link>
                        <span>{row.played}</span><span>{row.goal_difference > 0 ? `+${row.goal_difference}` : row.goal_difference}</span><strong>{row.points}</strong>
                    </div>
                ))}
            </div>
        </section>
    );
};

const Lineup = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const pageParams = new URLSearchParams(location.search);
    const requestedMatchUrl = pageParams.get("url") || requestedHockeyMatch(pageParams);
    const requestedReturn = pageParams.get("returnTo") || "";
    const backTarget = requestedReturn.startsWith("/tips?") || requestedReturn === "/tips" || requestedReturn.startsWith("/?") || requestedReturn.startsWith("/upcoming?") || requestedReturn === "/upcoming" || /^\/team\/\d+\?serie=[A-Za-z0-9_%.-]+$/.test(requestedReturn) ? requestedReturn : "/";
    const backToTips = backTarget.startsWith("/tips");
    const backToSchedule = backTarget.startsWith("/upcoming");
    const backToTeam = backTarget.startsWith("/team/");
    const requestedTab = pageParams.get("tab");
    const activeTab = ["events", "standings", "simulate"].includes(requestedTab) ? requestedTab : requestedTab === "table" ? "standings" : "lineup";
    const hideResults = window.localStorage.getItem(`${HQ.storagePrefix}-hide-results`) === "true";
    const [lineup, setLineup] = useState(null);
    const [error, setError] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [matchStatus, setMatchStatus] = useState(location.state?.match?.status || null);
    const matchUrl = resolveMatchUrl(requestedMatchUrl, matchStatus);
    const [statusLoading, setStatusLoading] = useState(!location.state?.match?.status);
    const [standings, setStandings] = useState(null);
    const [sportsimLeague, setSportsimLeague] = useState(null);
    const [standingsLoading, setStandingsLoading] = useState(false);
    const [standingsError, setStandingsError] = useState(false);
    const [forceRefresh] = useState(() => {
        const fresh = window.sessionStorage.getItem(`${HQ.storagePrefix}-force-refresh`) === "1";
        window.sessionStorage.removeItem(`${HQ.storagePrefix}-force-refresh`);
        return fresh;
    });
    const homeScore = location.state?.match?.status;
    const verifiedMismatch = useRef(false);

    useEffect(() => {
        const competitionId = matchStatus?.competition_id;
        const seasonId = matchStatus?.season_id;
        if (!competitionId || !seasonId) return undefined;
        let active = true;
        fetch(`${HQ.api}/sportsim/simulations`).then((response) => response.json()).then((rows) => {
            if (active && Array.isArray(rows)) setSportsimLeague(rows.find((row) => HQ.sport === "hockey" ? row.key === String(competitionId) : row.source_url?.includes(`/sasong/${seasonId}/serie/${competitionId}/`)) || null);
        }).catch(() => {});
        return () => { active = false; };
    }, [matchStatus?.competition_id, matchStatus?.season_id]);

    useEffect(() => {
        if (!matchUrl) {
            setError(true);
            return undefined;
        }
        if (activeTab !== "lineup" || HQ.sport === "hockey") return undefined;
        const controller = new AbortController();
        fetch(`${API_URL}?url=${encodeURIComponent(matchUrl)}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Nätverksfel");
                return response.json();
            })
            .then((data) => {
                setLineup(data);
                setError(false);
            })
            .catch((fetchError) => {
                if (fetchError.name === "AbortError") return;
                console.error("Fel vid hämtning av laguppställning:", fetchError);
                setError(true);
            });
        return () => controller.abort();
    }, [matchUrl, activeTab]);

    useEffect(() => {
        if (!matchUrl) return undefined;
        let cancelled = false;
        let controller = null;
        const loadStatus = async () => {
            controller?.abort();
            controller = new AbortController();
            try {
                const response = await fetch(
                    `${STATUS_API_URL}?url=${encodeURIComponent(matchUrl)}${forceRefresh ? "&fresh=true" : ""}`,
                    { signal: controller.signal, cache: forceRefresh ? "no-store" : "default" },
                );
                if (!response.ok) throw new Error("Nätverksfel");
                const data = await response.json();
                if (cancelled) return;
                const mismatched = homeScore && (data.home_goals !== homeScore.home_goals || data.away_goals !== homeScore.away_goals);
                setMatchStatus(data);
                if (HQ.sport === "hockey") setLineup({lineup:data.hockey_lineup});
                if (mismatched && !verifiedMismatch.current) {
                    const verified = await fetch(`${STATUS_API_URL}?url=${encodeURIComponent(matchUrl)}&fresh=true&verify=true`, { signal: controller.signal, cache: "no-store" });
                    if (!verified.ok) throw new Error("Nätverksfel");
                    const freshData = await verified.json();
                    if (!cancelled) {
                        setMatchStatus(freshData);
                        verifiedMismatch.current = true;
                        window.sessionStorage.setItem(`${HQ.storagePrefix}-force-refresh`, "1");
                    }
                }
            } catch (fetchError) {
                if (fetchError.name !== "AbortError") {
                    console.error("Fel vid hämtning av matchresultat:", fetchError);
                }
            } finally {
                if (!cancelled) setStatusLoading(false);
            }
        };
        loadStatus();
        const interval = setInterval(loadStatus, 60000);
        return () => {
            cancelled = true;
            controller?.abort();
            clearInterval(interval);
        };
    }, [matchUrl, forceRefresh, homeScore?.home_goals, homeScore?.away_goals]);

    useEffect(() => {
        if (!["standings", "simulate"].includes(activeTab) || !matchStatus?.competition_id || standings) return undefined;
        const controller = new AbortController();
        setStandingsLoading(true);
        fetch(`${STANDINGS_API_URL}?competition_id=${matchStatus.competition_id}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Nätverksfel");
                return response.json();
            })
            .then((data) => { setStandings(data); setStandingsError(false); })
            .catch((fetchError) => {
                if (fetchError.name !== "AbortError") setStandingsError(true);
            })
            .finally(() => setStandingsLoading(false));
        return () => controller.abort();
    }, [activeTab, matchStatus?.competition_id, standings]);

    const selectTab = (tab) => {
        const nextParams = new URLSearchParams(location.search);
        if (tab !== "lineup") nextParams.set("tab", tab);
        else nextParams.delete("tab");
        navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
    };

    return (
        <main className="lineup-page">
            <nav className="lineup-toolbar" aria-label="Navigering">
                <div className="lineup-back-links"><button className="lineup-button lineup-button--secondary" onClick={() => navigate(backTarget, { replace: true })}>
                    ← {backToTips ? "Tipsen" : backToSchedule ? "Spelschema" : backToTeam ? "Laget" : "Matcherna"}
                </button>{backToTips && <button className="lineup-home-link" onClick={() => navigate("/")}>Matchlistan</button>}</div>
                <div className="hq-page-actions"><ShareButton path={`/match?url=${encodeURIComponent(matchUrl || "")}&tab=${activeTab}`} title={matchStatus?.home_team && matchStatus?.away_team ? `${matchStatus.home_team} – ${matchStatus.away_team}` : "Matchcenter"} disabled={!matchStatus?.home_team} /><button className="lineup-button" onClick={() => window.open(matchStatus?.report_url || (HQ.sport === "floorball" ? matchUrl : sportsimLeague?.source_url), "_blank", "noopener,noreferrer")}>
                    Matchsidan ↗
                </button></div>
            </nav>
            <header className="lineup-page__header">
                <span className="lineup-page__kicker">{HQ.brand}</span>
                <h1>Matchcenter</h1>
                <p>{sportsimLeague && !sportsimLeague.closed
                    ? <Link className="lineup-page__league-link" to={`/standings?serie=${encodeURIComponent(sportsimLeague.key)}`}>{matchStatus?.competition_name || location.state?.match?.league || sportsimLeague.name}</Link>
                    : matchStatus?.competition_name || location.state?.match?.league || "Laguppställning och matchhändelser"}</p>
            </header>
            <MatchScore match={matchStatus} loading={statusLoading} hidden={hideResults && activeTab !== "events"} teamPath={matchStatus?.competition_id ? (id) => `/team/${encodeURIComponent(id)}?competition=${matchStatus.competition_id}&serie=${encodeURIComponent(sportsimLeague?.key || "")}&league=${encodeURIComponent(matchStatus?.competition_name || "")}&returnTo=${encodeURIComponent(location.pathname + location.search)}` : null} />
            <MatchInfo match={matchStatus} />
            {sportsimLeague && <MatchTipPulse simulation={sportsimLeague} matchId={HQ.sport === "hockey" ? matchStatus?.external_match_id : matchUrl?.match(/\/match\/(\d+)/)?.[1]} matchStatus={matchStatus} />}
            <div className="match-tabs" role="tablist" aria-label="Matchinformation">
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "lineup"}
                    className={activeTab === "lineup" ? "match-tab match-tab--active" : "match-tab"}
                    onClick={() => selectTab("lineup")}
                >
                    Lag
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "events"}
                    className={activeTab === "events" ? "match-tab match-tab--active" : "match-tab"}
                    onClick={() => selectTab("events")}
                >
                    Händelser
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "standings"}
                    className={activeTab === "standings" ? "match-tab match-tab--active" : "match-tab"}
                    onClick={() => selectTab("standings")}
                >
                    Tabell
                </button>
                {sportsimLeague && <button type="button" role="tab" aria-selected={activeTab === "simulate"} className={activeTab === "simulate" ? "match-tab match-tab--active" : "match-tab"} onClick={() => selectTab("simulate")}>Simulera</button>}
            </div>
            {activeTab === "simulate" ? (
                <MatchSimulation simulation={sportsimLeague} standings={standings} match={{ ...matchStatus, id: HQ.sport === "hockey" ? matchStatus?.external_match_id : matchUrl?.match(/\/match\/(\d+)/)?.[1] }} />
            ) : activeTab === "standings" ? (
                <Standings data={standings} loading={standingsLoading} error={standingsError} match={matchStatus} serie={sportsimLeague?.key || ""} returnTo={location.pathname + location.search} />
            ) : activeTab === "events" ? (
                <>
                  {HQ.sport === "hockey" && matchStatus?.hockey_stats && <div className="hockey-stat-strip">{[["shots","Skott"],["saves","Räddningar"],["pim","Utvisningsminuter"]].filter(([key]) => matchStatus.hockey_stats[key]).map(([key,label]) => <div key={key}><strong>{matchStatus.hockey_stats[key].home}</strong><span>{label}</span><strong>{matchStatus.hockey_stats[key].away}</strong></div>)}</div>}
                  <MatchEvents match={matchStatus} loading={statusLoading} onSelectPlayer={setSelectedPlayer} />
                </>
            ) : error ? (
                    <p className="error-message">⚠️ Kunde inte hämta laguppställning. Prova att ladda om sidan.</p>
                ) : !lineup ? (
                    <div className="loading-container">
                        <div className="loader"></div>
                        <p className="loading-text">Hämtar laguppställning…</p>
                    </div>
                ) : HQ.sport === "hockey" ? <HockeyLineups lineup={lineup.lineup} logos={[matchStatus?.home_logo,matchStatus?.away_logo]} /> : (
                    <div className="teams-lineup-grid">
                        <TeamLineup team={{ ...lineup.home_team, name: matchStatus?.home_team || location.state?.match?.home_team || (lineup.home_team.name !== "N/A" ? lineup.home_team.name : "Hemmalag"), logo: matchStatus?.home_logo || location.state?.match?.home_logo || lineup.home_team.logo }} onSelectPlayer={setSelectedPlayer} />
                        <TeamLineup team={{ ...lineup.away_team, name: matchStatus?.away_team || location.state?.match?.away_team || (lineup.away_team.name !== "N/A" ? lineup.away_team.name : "Bortalag"), logo: matchStatus?.away_logo || location.state?.match?.away_logo || lineup.away_team.logo }} onSelectPlayer={setSelectedPlayer} />
                    </div>
                )}
            {HQ.sport === "hockey" && <div className="hockey-source-links">{matchStatus?.lineup_url && <a href={matchStatus.lineup_url} target="_blank" rel="noreferrer">Original laguppställning ↗</a>}{matchStatus?.player_stats_url && <a href={matchStatus.player_stats_url} target="_blank" rel="noreferrer">Spelarstatistik ↗</a>}</div>}
            {selectedPlayer && (
                <PlayerProfileWidget
                    key={selectedPlayer.id || selectedPlayer.name}
                    player={selectedPlayer}
                    onClose={() => setSelectedPlayer(null)}
                    onPositionChange={(position, ibis) => {
                        const update = (player) => player.id === selectedPlayer.id
                            ? { ...player, ibis_position: player.ibis_position ?? player.position ?? ibis, community_position: position, position: position ?? player.ibis_position ?? player.position ?? ibis }
                            : player;
                        setSelectedPlayer(update);
                        setLineup((current) => current ? { ...current,
                            home_team: { ...current.home_team, players: current.home_team.players.map(update) },
                            away_team: { ...current.away_team, players: current.away_team.players.map(update) },
                        } : current);
                    }}
                />
            )}
        </main>
    );
};

export default Lineup;
